import React, { useEffect, useMemo, useState } from "react";

import clsx from "clsx";
import { motion } from "framer-motion";
import { isEmpty } from "lodash";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { type TableHeaderRow } from "@devexpress/dx-react-grid-bootstrap4";
import { usePageConfiguration } from "@features/sales-v3/hooks";
import { type ColumnDef } from "@features/sales-v3/hooks/use-table-columns";
import {
  CreativeMetricType,
  CreativesFilterClauseTypeEnum,
  CreativesSortingOrder,
  type CreativesStatusEnum,
  type SalesTableReportV3FilterMetricClauseInput,
  type SalesTableReportV3MetricsFilterInput,
  type SalesPageMetric,
} from "@north-beam/nb-client-side";

import { FIXED_NAME_COLUMN_ID, FIXED_STATUS_COLUMN_ID } from "../constants";

import { ColumnMenu } from "./column-menu";
import { NumericFilterPopover } from "./numeric-filter-popover";
import { StatusFilterPopover } from "./status-filter-popover";

const LEFT_ALIGNED = [FIXED_NAME_COLUMN_ID, FIXED_STATUS_COLUMN_ID];

export const TableHeaderContent = ({
  column,
  children,
}: TableHeaderRow.ContentProps) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const columnDef = column as ColumnDef;
  const {
    sorting,
    sortingOrder,
    setSorting,
    setSortingOrder,
    metricFilters,
    setMetricFilters,
    accountingMode,
    attributionModel,
    attributionWindow,
    statuses,
    setStatuses,
  } = usePageConfiguration();

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const existingMetricFilter = useMemo(() => {
    if (columnDef.name !== FIXED_STATUS_COLUMN_ID) {
      return metricFilters.metricClauses?.find(
        (metricClause) => metricClause.metric.id === columnDef.id,
      );
    }
    return null;
  }, [columnDef.id, columnDef.name, metricFilters.metricClauses]);

  const existingStatusFilter = useMemo(() => {
    if (columnDef.name === FIXED_STATUS_COLUMN_ID) {
      return statuses?.length;
    } else {
      return null;
    }
  }, [columnDef.name, statuses?.length]);

  const hasFilter = useMemo(() => {
    if (columnDef.name !== FIXED_STATUS_COLUMN_ID) {
      return Boolean(existingMetricFilter);
    }
    return Boolean(existingStatusFilter);
  }, [columnDef.name, existingMetricFilter, existingStatusFilter]);

  const [sort, setSort] = useState<CreativesSortingOrder | null>(null);
  const [filter, setFilter] = useState(hasFilter);

  useEffect(() => {
    setFilter(hasFilter);
  }, [hasFilter]);

  useEffect(() => {
    if (columnDef) {
      const { metric } = columnDef;

      if (isEmpty(sorting)) {
        return setSort(null);
      }

      if (sorting.id !== columnDef.id) {
        setSort(null);
      } else if (
        // This conditional is for custom columns to update the sorting when changed
        sorting?.type === CreativeMetricType.user_defined &&
        sorting.id === columnDef.id &&
        sorting.metric.metric !== metric?.composition?.metric
      ) {
        setSorting({
          customComposed: metric.composition.customComposed,
          metric: {
            id: metric.id,
            metric: metric.composition.metric,
            details: {
              accountingMode,
              am: attributionModel,
              aw: attributionWindow,
              type: "",
              ...(metric.composition.details && {
                ...metric.composition.details,
              }),
            },
          },
          id: metric.id,
          type: metric.composition.type,
        });
      } else {
        setSort(sortingOrder);
      }
    }
  }, [
    columnDef,
    sorting,
    sortingOrder,
    accountingMode,
    attributionModel,
    attributionWindow,
    setSorting,
  ]);

  const updateSorting = (
    metric: SalesPageMetric,
    direction: CreativesSortingOrder,
  ) => {
    if (columnDef.name === FIXED_NAME_COLUMN_ID) {
      setSorting({
        customComposed: true,
        metric: {
          id: columnDef.name,
          metric: columnDef.name,
          details: {
            accountingMode,
            am: attributionModel,
            aw: attributionWindow,
            type: "",
          },
        },
        id: columnDef.name,
        type: CreativeMetricType.custom,
      });
      setSortingOrder(direction);
      return;
    }
    setSorting({
      customComposed: metric.composition.customComposed,
      metric: {
        id: metric.id,
        metric: metric.composition.metric,
        details: {
          accountingMode,
          am: attributionModel,
          aw: attributionWindow,
          type: "",
          ...(metric.composition.details && {
            ...metric.composition.details,
          }),
        },
      },
      id: metric.id,
      type: metric.composition.type,
    });
    setSortingOrder(direction);
  };

  return (
    <>
      <div
        className={clsx("text-xs w-full flex items-center px-2", {
          "justify-end": !LEFT_ALIGNED.includes(columnDef.name),
        })}
      >
        <span
          className={clsx("overflow-hidden min-w-[10px] flex items-center", {
            "flex-1": LEFT_ALIGNED.includes(columnDef.name),
          })}
        >
          {columnDef.metric?.composition?.type ===
            CreativeMetricType.user_defined && (
            <i className="fa-solid fa-sharp fa-function text-xxs text-cosmic mr-1" />
          )}
          {children}
        </span>
        <ColumnMenu
          column={columnDef}
          onSelect={(val) => {
            if (val === "asc" || val === "desc") {
              prettyLogEventPage({
                domain: "Table",
                action: "onChange",
                additionalActionInfo: "Change Sorting",
              });

              if (sort === val) {
                setSortingOrder(null);
              } else {
                const metric = columnDef.metric;
                updateSorting(metric, val as CreativesSortingOrder);
              }

              // if we want to remove sorting
              if (sort === val) {
                setSorting(null);
              }
            }

            if (val === "filter") {
              prettyLogEventPage({
                domain: "Table",
                action: "onChange",
                additionalActionInfo: "Change Filtering",
              });

              if (filter) {
                setFilter(false);
                setIsFilterOpen(false);

                if (columnDef.name === FIXED_STATUS_COLUMN_ID) {
                  setStatuses(null);
                } else {
                  const newFilterConfig: SalesTableReportV3MetricsFilterInput =
                    {
                      metricClauses: metricFilters.metricClauses ?? [],
                      clauseType:
                        metricFilters.clauseType ??
                        CreativesFilterClauseTypeEnum.AND,
                    };
                  const newFilterMetricsFiltered =
                    newFilterConfig.metricClauses.filter(
                      (metricClause) => metricClause.metric.id !== columnDef.id,
                    );
                  setMetricFilters({
                    metricClauses: newFilterMetricsFiltered,
                    clauseType: newFilterConfig.clauseType,
                  });
                }
              } else {
                setFilter(true);
                setIsFilterOpen(true);
              }
            }
          }}
        />
        {filter && (
          <>
            {/* Unused for now */}
            {/* {columnDef.name === FIXED_NAME_COLUMN_ID && (
              <TagsFilterPopover onReset={() => setFilter(false)} />
            )} */}
            {columnDef.name === FIXED_STATUS_COLUMN_ID && (
              <StatusFilterPopover
                isOpen={isFilterOpen}
                onOpen={() => setIsFilterOpen(true)}
                onClose={() => {
                  setIsFilterOpen(false);
                  if (!statuses) {
                    setFilter(false);
                  }
                }}
                onReset={() => {
                  prettyLogEventPage({
                    domain: "Table",
                    action: "Other",
                    additionalActionInfo: "Reset Status Filtering",
                  });

                  setStatuses(null);
                  setFilter(false);
                }}
                onApply={function (
                  newStatuses: CreativesStatusEnum[] | null,
                ): void {
                  prettyLogEventPage({
                    domain: "Table",
                    action: "Other",
                    additionalActionInfo: "Change Filtering by Status",
                    data: {
                      newStatuses,
                      pageContext: { statuses },
                    },
                  });

                  setStatuses(newStatuses);
                }}
                currentStatuses={statuses}
              />
            )}
            {columnDef.name !== FIXED_NAME_COLUMN_ID &&
              columnDef.name !== FIXED_STATUS_COLUMN_ID && (
                <NumericFilterPopover
                  isOpen={isFilterOpen}
                  onOpen={() => setIsFilterOpen(true)}
                  onClose={() => {
                    setIsFilterOpen(false);
                    if (!existingMetricFilter?.value) {
                      setFilter(false);
                    }
                  }}
                  onReset={() => {
                    prettyLogEventPage({
                      domain: "Table",
                      action: "Other",
                      additionalActionInfo: "Reset Filtering",
                    });

                    if (metricFilters.metricClauses) {
                      const newFilterConfig: SalesTableReportV3MetricsFilterInput =
                        {
                          metricClauses: metricFilters.metricClauses ?? [],
                          clauseType:
                            metricFilters.clauseType ??
                            CreativesFilterClauseTypeEnum.AND,
                        };
                      const newFilterMetricsFiltered =
                        newFilterConfig.metricClauses.filter(
                          (metricClause) =>
                            metricClause.metric.id !== columnDef.id,
                        );
                      setMetricFilters({
                        metricClauses: newFilterMetricsFiltered,
                        clauseType: newFilterConfig.clauseType,
                      });
                    }

                    setFilter(false);
                  }}
                  operator={existingMetricFilter?.operator}
                  value={existingMetricFilter?.value}
                  onApply={(
                    value: number,
                    operator: SalesTableReportV3FilterMetricClauseInput["operator"],
                  ) => {
                    prettyLogEventPage({
                      domain: "Table",
                      action: "Other",
                      additionalActionInfo: "Apply Filtering",
                    });

                    //
                    const newFilterConfig: SalesTableReportV3MetricsFilterInput =
                      {
                        metricClauses: metricFilters.metricClauses ?? [],
                        clauseType:
                          metricFilters.clauseType ??
                          CreativesFilterClauseTypeEnum.AND,
                      };
                    const newFilterMetricsFiltered =
                      newFilterConfig.metricClauses.filter(
                        (metricClause) =>
                          metricClause.metric.id !== columnDef.id,
                      );
                    const metric = columnDef.metric;

                    newFilterMetricsFiltered.push({
                      customComposed: metric.composition.customComposed,
                      metric: {
                        id: metric.id,
                        metric: metric.composition.metric,
                        details: {
                          accountingMode,
                          am: attributionModel,
                          aw: attributionWindow,
                          type: "",
                          ...(metric.composition.details && {
                            ...metric.composition.details,
                          }),
                        },
                      },

                      type: metric.composition.type,
                      value: Number(value),
                      operator,
                    });

                    setMetricFilters({
                      ...newFilterConfig,
                      metricClauses: newFilterMetricsFiltered,
                    });
                  }}
                />
              )}
          </>
        )}
        {sort && (
          <div
            className={clsx(
              "flex justify-center items-center bg-cosmic-3",
              "rounded w-6 h-6 ml-0.5 cursor-pointer text-cosmic",
            )}
            onClick={() => {
              updateSorting(
                columnDef.metric,
                sort === "desc"
                  ? CreativesSortingOrder.asc
                  : CreativesSortingOrder.desc,
              );
            }}
          >
            <motion.i
              initial={false}
              animate={{ rotate: sort === "desc" ? 0 : 180 }}
              transition={{ duration: 0.2 }}
              className="fa-regular fa-sharp fa-arrow-down"
            />
          </div>
        )}
      </div>
      <div className="header-column-separator"></div>
    </>
  );
};
